import {useRef} from 'react'
import getBoundingClientRects from '@/script/getBoundingClientRects.mjs'
import usePressedKeys from '@/hooks/usePressedKeys.mjs'
import {setTimeout} from '@/script/timer.mjs'

const DBL_CLICK_INTERVAL = 500
const DBL_CLICK_OFFSET = 5

export default () => {
    const pressedKeys = usePressedKeys()
    const refRect = useRef({})
    const refLastClick = useRef(null)
    const refLastNode = useRef(null)
    const refIsActive = useRef(true)
    const refLockEvent = useRef(0)

    const scrollNodeIntoViewIfNeeded = async (map, tree, node) => {
        // 必须在异步获取画布等尺寸前读取 scrollLeft 和 scrollTop,
        // 否则如果一次事件循环中多次调用此函数可能会导致前次设置的
        // scrollLeft 和 scrollTop 被后续读取，导致错误的最终结果
        const {
            clientWidth,
            clientHeight,
            scrollLeft,
            scrollTop,
        } = map.canvas

        const [
            canvasRect,
            treeRect,
            nodeRect,
        ] = await getBoundingClientRects([map.canvas, tree, node])

        const isFullyVisible = (
            canvasRect.left <= nodeRect.left &&
            // 不直接使用 canvasRect.right 是兼顾有滚动条的情况
            nodeRect.right <= canvasRect.left + clientWidth &&
            canvasRect.top <= nodeRect.top &&
            // 不直接使用 canvasRect.bottom 是兼顾有滚动条的情况
            nodeRect.bottom <= canvasRect.top + clientHeight
        )

        if (isFullyVisible) {
            return
        }

        const [newScrollLeft, newScrollTop] = (() => {
            const getScrollOffset = (
                viewSize,
                treeSize,
                treeOffset,
                nodeSize,
                nodeOffset,
            ) => {
                // 可视区小于等于节点时，使节点起始可见
                if (viewSize <= nodeSize) {
                    // 为美观保留的空白
                    const MARGIN = 8
                    return nodeOffset - MARGIN
                }
                // 树小于等于可视区时，使树相对可视区居中
                else if (treeSize <= viewSize) {
                    const offsetTreeToView = (viewSize - treeSize) / 2
                    return treeOffset - offsetTreeToView
                }
                // 树大于可视区时，在保证不留白的情况下尽量使被选中节点居中
                else {
                    const offsetNodeToView = (viewSize - nodeSize) / 2
                    const offsetNodeToTree = nodeOffset - treeOffset

                    // 左/上边有留白
                    if (offsetNodeToTree < offsetNodeToView) {
                        // 使树左/上侧与可视区左/上侧重合
                        return treeOffset
                    }

                    const offsetNodeToTreeRight = treeSize - nodeSize
                        - offsetNodeToTree

                    // 右/下边有留白
                    if (offsetNodeToTreeRight < offsetNodeToView) {
                        // 使树右/下侧与可视区右/下侧重合
                        return treeOffset + treeSize - viewSize
                    }

                    // 没有留白
                    return nodeOffset - offsetNodeToView
                }
            }

            return [
                getScrollOffset(
                    clientWidth,
                    treeRect.width,
                    treeRect.left - canvasRect.left + scrollLeft,
                    nodeRect.width,
                    nodeRect.left - canvasRect.left + scrollLeft,
                ),

                getScrollOffset(
                    clientHeight,
                    treeRect.height,
                    treeRect.top - canvasRect.top + scrollTop,
                    nodeRect.height,
                    nodeRect.top - canvasRect.top + scrollTop,
                ),
            ]
        })()

        map.canvas.scrollLeft = newScrollLeft
        map.canvas.scrollTop = newScrollTop
    }

    const scrollNodeIntoView = async map => {
        if (! refIsActive.current) {
            return
        }

        // 等待 DOM 节点注册完成
        await setTimeout()

        const lastNode = [...map.selectedNodes].at(-1)

        if (map.isNodeEditingText(lastNode)) {
            return
        }

        const rect = (() => {
            const {
                clientHeight,
                clientWidth,
                scrollHeight,
                scrollWidth,
            } = map.canvas

            const r = refRect.current

            if (
                clientHeight !== r.clientHeight ||
                clientWidth !== r.clientWidth ||
                scrollHeight !== r.scrollHeight ||
                scrollWidth !== r.scrollWidth
            ) {
                return {
                    clientHeight,
                    clientWidth,
                    scrollHeight,
                    scrollWidth,
                }
            }
            else {
                return r
            }
        })()

        if (
            lastNode &&

            (
                rect !== refRect.current ||
                lastNode !== refLastNode.current
            )
        ) {
            const {nodes, trees} = map.dom
            const tree = trees.get(map.root)
            const node = nodes.get(lastNode)
            scrollNodeIntoViewIfNeeded(map, tree, node)
        }

        refRect.current = rect
        refLastNode.current = lastNode
    }

    const watchers = {
        canvas_click_capture(e) {
            if (1 === refLockEvent.current) {
                refLockEvent.current = 2
                e.preventDefault()
                e.stopPropagation()
                const ee = new MouseEvent('click', e)
                refLastClick.current.target.dispatchEvent(ee)
            }
        },

        canvas_double_click_capture(e) {
            if (2 === refLockEvent.current) {
                refLockEvent.current = 0
                e.preventDefault()
                e.stopPropagation()
                const ee = new MouseEvent('dblclick', e)
                refLastClick.current.target.dispatchEvent(ee)
            }

            refLastClick.current = null
        },

        canvas_pointer_down_capture(e) {
            if (! (
                refLastClick.current &&
                0 === e.button
            )) {
                return
            }

            const {
                clientX,
                clientY,
                target,
                timeStamp,
            } = refLastClick.current

            if (
                e.target !== target &&
                e.timeStamp - timeStamp < DBL_CLICK_INTERVAL &&
                Math.abs(e.clientX - clientX) < DBL_CLICK_OFFSET &&
                Math.abs(e.clientY - clientY) < DBL_CLICK_OFFSET
            ) {
                e.preventDefault()
                e.stopPropagation()
                const ee = new PointerEvent('pointerdown', e)
                target.dispatchEvent(ee)
            }
        },

        //canvas_pointer_move_capture(e) {
            //refLockEvent.current = false
        //},

        canvas_pointer_up_capture(e) {
            if (! (
                refLastClick.current &&
                0 === e.button
            )) {
                return
            }

            const {
                clientX,
                clientY,
                target,
                timeStamp,
            } = refLastClick.current

            if (
                e.target !== target &&
                e.timeStamp - timeStamp < DBL_CLICK_INTERVAL &&
                Math.abs(e.clientX - clientX) < DBL_CLICK_OFFSET &&
                Math.abs(e.clientY - clientY) < DBL_CLICK_OFFSET
            ) {
                e.preventDefault()
                e.stopPropagation()
                const ee = new PointerEvent('pointerup', e)
                target.dispatchEvent(ee)
                refLockEvent.current = 1
            }
        },

        canvas_resize() {
            scrollNodeIntoView(this)
        },

        canvas_swipe_end() {
            refIsActive.current = true
        },

        canvas_swipe_start() {
            refIsActive.current = false
        },

        model_change() {
            scrollNodeIntoView(this)
        },

        node_click({event, node}) {
            const {button, clientX, clientY, target, timeStamp} = event

            if (0 !== button) {
                return
            }

            refLastClick.current = {
                clientX,
                clientY,
                node,
                target,
                timeStamp,
            }
        },

        selected_nodes_change() {
            const {alt, ctrl, meta, shift, other} = pressedKeys

            if (
                ! ctrl ||
                alt ||
                meta ||
                shift ||
                0 < other.size
            ) {
                scrollNodeIntoView(this)
            }
        },
    }

    return {watchers}
}
